import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    ItemBetName,
    NotificationListItem,
    ItemBetTime,
} from 'src/components/Header/LHSMenu/NotificationsMenu/NotificationsMenuLists/NotificationsMenuLists.style';
import { ChatRequestNotificationType, ChatResponseSchemaType } from 'src_server/types/notificationData';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { action, computed, makeAutoObservable, observable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { Button } from 'src/domains/common/components/button/button';
import { useAppStateContext } from 'src/AppState/AppState';

const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '100px',
    threshold: 0.15,
};

class ChatRequestNotificationState {
    private readonly observer: IntersectionObserver | null = null;
    public readonly lastMsgResource: Resource<ChatResponseSchemaType>;
    @observable public msgRef: HTMLImageElement | null = null;
    @observable public allowLoad: boolean = false;

    public constructor(public readonly chatId: string) {
        this.observer = new IntersectionObserver(this.loadLastMsg, observerOptions);
        this.lastMsgResource = new Resource(async (): Promise<ChatResponseSchemaType> => {
            return await $appState.trpcClient.client.notification.getChatLastMessage.query({ chatId: this.chatId });
        });
        makeAutoObservable(this);
    }

    public setMsgRef = (ref: HTMLImageElement | null): void => {
        this.msgRef = ref;
    };

    public observe = (): void => {
        if (this.msgRef !== null && this.observer !== null) {
            this.observer.observe(this.msgRef);
        }
    };

    public unobserve = (): void => {
        if (this.observer !== null) {
            this.observer.disconnect();
        }
    };

    @action private loadLastMsg = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
                this.allowLoad = true;
                this.observer?.unobserve(entry.target);
            }
        });
    };

    @computed public get lastMessage(): string {
        if (this.allowLoad) {
            const msg = this.lastMsgResource.get();
            if (msg.type === 'ready') {
                return msg.value.content?.text ?? '';
            }

            if (msg.type === 'loading') {
                return 'loading';
            }
        }

        return '';
    }
}

interface NotificationsMenuChatItemType {
    chatItem: ChatRequestNotificationType;
}

export const NotificationsMenuChatItem = observer(
    'NotificationsMenuChatItem',
    ({ chatItem }: NotificationsMenuChatItemType): JSX.Element | null => {
        const { status, createdAt, details } = chatItem;
        const appState = useAppStateContext();
        const [state] = useState(() => new ChatRequestNotificationState(chatItem.details.chatId));

        React.useEffect(() => {
            state.observe();
            return (): void => state.unobserve();
        }, []);

        const age = DateTime.from(createdAt)?.fromNow();

        const sender = details.participants.find((p) => p.role === 'owner');

        const chatId = details.chatId;
        const senderName = sender?.account.name;

        const onClick = (): void => {
            appState.routerBackoffice.redirectToSpecificUserChat(chatId);
        };

        return (
            <NotificationListItem
                ref={state.setMsgRef}
                status={status}
                className='rounded-md border border-border border-solid bg-background'
            >
                <ItemBetName className='text-xs text-foreground'>{state.lastMessage}</ItemBetName>
                <ItemBetTime className='text-muted-foreground text-[0.625rem] leading-[1.40]'>
                    {senderName} {age}
                </ItemBetTime>
                <Button
                    variant='outline'
                    className='w-full'
                    type='button'
                    onClick={onClick}
                    // TODO ADD ROUTE AFTER USER PORTAL
                >
                    Chat
                </Button>
            </NotificationListItem>
        );
    }
);
